import React, { FC, useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import TwInitialsIndicator from '../../TwInitialsIndicator/TwInitialsIndicator';
import TwGrossPayTooltip from '../../TwGrossPayTooltip/TwGrossPayTooltip';
import NumberUtils from '../../../utils/numberUtils';
import { LOAD_TYPE } from '../../../redux/api/load/load.model';
import DriverPayUtils from '../../../utils/driverPayUtils';
import StringUtils from '../../../utils/stringUtils';
import useAuth from '../../../providers/Auth/Auth.provider';
import useFeatureFlags from '../../../providers/FeatureFlags/FeatureFlags.provider';
import { USER_ROLE } from '../../../providers/User/User.model';
import TwTotalMilage from '../../TwLoadDetail/components/TotalMilage/TwTotalMilage';
import LoadUtils from '../../../utils/loadUtils';
import Typography from '@mui/material/Typography';
import TwLoadTooltipContent from '../../TwLoadTooltipInfo/TwLoadTooltipContent';
import TwLoadTooltipInfo, {
  TwLoadTooltipInfoProps
} from '../../TwLoadTooltipInfo/TwLoadTooltipInfo';
import { Trip, TripDriver, TRIP_STATUS } from '../../../redux/api/trips/trips.model';
import { Order } from '../../../redux/api/orders/orders.model';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { setOrderId } from '../../../redux/features/orders/ordersSlice';
import TwLoadTags from '../../TwLoadTag/TwLoadTag';
import { EntityType } from '../../../redux/Global.model';
import SettlementStatusLabels from '../../TwLoadDetail/components/SettlementStatus/SettlementStatus';
import { MANIFEST_TYPE, TripManifest } from '../../../redux/api/manifest/manifest.model';
import DateUtils from '../../../utils/dateUtils';
import { TwManifestErrorTooltip } from '../components/TwManifestErrorTooltip';
import { useGetTripByIdQuery } from '../../../redux/api/trips/tripApi';

const TripOverview: FC<{
  segmentData: Trip;
  tripOrders: Order[];
  readableId?: string | number;
  ordersTooltipDataArray: TwLoadTooltipInfoProps[];
  orderType: LOAD_TYPE;
  changeTab?: (event: React.SyntheticEvent, newValue: string, isClickFromTab: boolean) => void;
  reloadData?: () => void;
}> = ({
  segmentData,
  ordersTooltipDataArray,
  readableId,
  tripOrders,
  orderType,
  changeTab,
  reloadData
}): JSX.Element => {
  const {
    data: { userData },
    getTenantSetting
  } = useAuth();
  const enableLtl = userData?.tenant?.ltl_enabled || false;
  const dispatch = useAppDispatch();
  const isOrderOverlayOpen = useAppSelector((state) => state.navigation.isLoadDetailOpen);
  const isTripOverlayOpen = useAppSelector((state) => state.navigation.isTripDetailOpen);

  const {
    enableDriverGrossPayEstimate,
    enableFlexibleTagging,
    enableBorderConnect,
    enableHideGoogleMapsAndRoutingCalculations
  } = useFeatureFlags();
  const carriersEnabled = getTenantSetting('multiple-carriers');
  const advancedDriverFieldsEnable = getTenantSetting('advanced-driver-fields');
  const isEmptyMilesEnabled = getTenantSetting('empty-miles');
  const tagsAreSeparated = getTenantSetting('separate-tag-management-for-orders-and-trips');
  const dispatcherEnable = getTenantSetting('multiple-dispatchers');
  const driversToEditMilesEnabled = getTenantSetting('allow-drivers-to-edit-miles');
  const isHideDriverInfoFromCustomersEnabled = getTenantSetting('hide-driver-info-customer-link');

  const [mostRecentManifest, setMostRecentManifest] = useState<TripManifest | null>(null);

  const isDriver = userData?.role === USER_ROLE.DRIVER;
  const isCarrier = userData?.role === USER_ROLE.CARRIER;
  const isCustomerContact = userData?.role === USER_ROLE.CUSTOMER_CONTACT;
  const isDriverOrCarrier = isDriver || isCarrier;

  const { data: trip } = useGetTripByIdQuery(segmentData.id, {
    skip: !segmentData.id
  });

  useEffect(() => {
    if (!trip?.trip_manifests) return;
    const sortedManifests = [...trip.trip_manifests].sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );

    setMostRecentManifest(sortedManifests[0]);
  }, [enableBorderConnect, trip, JSON.stringify(trip?.trip_manifests)]);

  const doShowDriverPay = (driver: TripDriver): boolean => {
    return (!isDriver || userData?.driver?.id === driver.id) && !isCustomerContact;
  };

  const handleOrderClick = (e: React.SyntheticEvent, orderId: string): void => {
    if (enableLtl) {
      if (isOrderOverlayOpen) {
        dispatch(setOrderId(orderId));
        changeTab && changeTab(e, '0', true);
        return;
      }
      if (isTripOverlayOpen) {
        changeTab && changeTab(e, orderId, false);
      }
    } else {
      changeTab && changeTab(e, orderId, false);
    }
  };

  const totalMileage = useMemo(() => {
    let total = 0;
    if (segmentData.estimated_mileage) {
      total += segmentData.estimated_mileage;
    }
    if (segmentData.empty_miles && isEmptyMilesEnabled) {
      total += segmentData.empty_miles;
    }

    return total;
  }, [segmentData.estimated_mileage, segmentData.empty_miles]);

  const formatManifestCreatedDate = (manifestType: MANIFEST_TYPE, date: string | Date): string => {
    const newDate = new Date(date);

    const formattedDate = DateUtils.getMediumDate(newDate);

    return `${manifestType} sent on ${formattedDate}`;
  };

  return (
    <Box component="div" className="load-detail__data-container__overview">
      <Box component="div" className="load-detail__data-container__overview--title">
        Overview
      </Box>

      {enableFlexibleTagging && !isCustomerContact && (
        <>
          <Box component="div" className="load-detail__data-container__overview--sub-title">
            Tags
          </Box>
          <TwLoadTags
            entity={tagsAreSeparated ? EntityType.TRIP : EntityType.ORDER}
            loadId={''}
            tripId={segmentData.id}
            tags={segmentData.tags}
            readOnly={!tagsAreSeparated}
            containerSx={{ marginBottom: '1rem' }}
            reloadData={reloadData}
          />
        </>
      )}

      {segmentData &&
        segmentData.drivers &&
        segmentData.drivers.length > 0 &&
        segmentData.drivers.map((driver, i) => (
          <Box key={`driver-overview-${driver.id}`}>
            <Box component="div" className="load-detail__data-container__overview--sub-title">
              {segmentData.drivers.length === 1 ? `Driver` : `Driver ${i + 1}`}
            </Box>
            <Box component="div" className="load-detail__data-container__overview__driver">
              <TwInitialsIndicator text={`${driver.first_name} ${driver.last_name}`} />
              <Box component="div" className="load-detail__data-container__overview__driver--data">
                <Box component="span">{`${driver.first_name} ${driver.last_name}`}</Box>
                <Box component="span">
                  {isHideDriverInfoFromCustomersEnabled && isCustomerContact ? (
                    <></>
                  ) : driver.phone ? (
                    StringUtils.formatPhoneNumber(driver.phone.toString())
                  ) : (
                    driver.email_address
                  )}
                </Box>
              </Box>
            </Box>

            {enableDriverGrossPayEstimate &&
              advancedDriverFieldsEnable &&
              (!!driver.driver_pay_override ||
                (driver.trip_driver_pay_other_payment &&
                  DriverPayUtils.getOtherPayItemsTotalAmount(driver.trip_driver_pay_other_payment) >
                    0)) &&
              doShowDriverPay(driver) && (
                <>
                  <Box component="div" className="load-detail__data-container__overview--sub-title">
                    Driver pay estimate
                  </Box>

                  <Box
                    component="div"
                    className="load-detail__data-container__overview__rate"
                    justifyContent="flex-start"
                    alignItems="center"
                  >
                    {/* We need to add the other pay items total amount to the driver pay override,
                      to simulate the sum which will be re-calculated in the BE when the load is saved */}

                    {NumberUtils.getCurrency(
                      (driver.driver_pay_override_data?.gross_pay_estimate || 0) +
                        DriverPayUtils.getOtherPayItemsTotalAmount(
                          driver.trip_driver_pay_other_payment || []
                        )
                    )}

                    {driver.driver_pay_override_data && (
                      <TwGrossPayTooltip
                        numberOfDrivers={segmentData.drivers.length}
                        loadType={orderType}
                        grossPayData={driver.driver_pay_override_data}
                        otherPayItemsTotalAmount={DriverPayUtils.getOtherPayItemsTotalAmount(
                          driver.trip_driver_pay_other_payment || []
                        )}
                      />
                    )}
                  </Box>
                </>
              )}
          </Box>
        ))}

      {!enableHideGoogleMapsAndRoutingCalculations &&
        segmentData &&
        (totalMileage !== 0 || (driversToEditMilesEnabled && isDriver)) &&
        !isCarrier &&
        !isCustomerContact &&
        (!isDriver || driversToEditMilesEnabled) && (
          <TwTotalMilage
            totalMileage={totalMileage}
            emptyMileage={segmentData.empty_miles || 0}
            loadedMileage={segmentData.estimated_mileage || 0}
            tooltipText={
              segmentData.is_empty_miles_auto
                ? 'Calculated based on last known location to the first stop'
                : 'Manually calculated'
            }
            tripId={segmentData.id}
            reloadData={reloadData}
            isDriver={isDriver}
            isEmptyMilesEnabled={isEmptyMilesEnabled}
            editable={!segmentData.has_driver_settlement}
          />
        )}

      {segmentData && segmentData.carrier && carriersEnabled && (
        <>
          <Box component="div" className="load-detail__data-container__overview--sub-title">
            Carrier
          </Box>
          <Box component="div" className="load-detail__data-container__overview__client-load">
            {segmentData.carrier.name}
          </Box>
        </>
      )}

      <Box component="div" className="load-detail__data-container__overview--sub-title">
        Trip #
      </Box>
      <Box component="div" className="load-detail__data-container__overview__client-load">
        {!enableLtl ? `${readableId}-${segmentData.sort_order + 1}` : `${readableId}`}
      </Box>

      <Box component="div" className="load-detail__data-container__overview--sub-title">
        Trip status
      </Box>
      <Box component="div" className="load-detail__data-container__overview__client-load">
        {LoadUtils.getTripStatusReadableText(segmentData.status || TRIP_STATUS.DRAFT)}
      </Box>

      {enableBorderConnect && (
        <>
          <Box component="div" className="load-detail__data-container__overview--sub-title">
            Border-crossing manifest
          </Box>
          <Box
            component="div"
            className="load-detail__data-container__overview__client-load"
            style={{ flexDirection: 'column', alignItems: 'flex-start' }}
          >
            {mostRecentManifest && <TwManifestErrorTooltip manifest={mostRecentManifest} />}
            {mostRecentManifest
              ? formatManifestCreatedDate(
                  mostRecentManifest.manifest_type,
                  mostRecentManifest.created_at
                )
              : 'None'}
          </Box>

          {mostRecentManifest && (
            <>
              <Box component="div" className="load-detail__data-container__overview--sub-title">
                Control number
              </Box>
              <Box component="div" className="load-detail__data-container__overview__client-load">
                {mostRecentManifest.control_number || ''}
              </Box>
            </>
          )}
        </>
      )}

      <Box component="div" className="load-detail__data-container__overview--sub-title">
        Settlement status
      </Box>
      <SettlementStatusLabels
        settlements={segmentData.settlements || []}
        drivers={segmentData.drivers || []}
      />

      {segmentData.dispatcher && dispatcherEnable && (
        <>
          <Box component="div" className="load-detail__data-container__overview--sub-title">
            Dispatcher
          </Box>
          <Box component="div" className="load-detail__data-container__overview__client-load">
            {segmentData.dispatcher.name}
          </Box>
        </>
      )}

      {!isDriverOrCarrier && !isCustomerContact && (
        <>
          <Box component="div" className="load-detail__data-container__overview--sub-title">
            Order #
          </Box>
          <Box component="div">
            {tripOrders?.map((order, i) => (
              <>
                <Box key={`order-overview-${order.id}`} component="span">
                  <TwLoadTooltipInfo
                    tooltipContent={
                      <TwLoadTooltipContent
                        data={ordersTooltipDataArray.find((d) => d.loadId === order.id)}
                      />
                    }
                  >
                    <Typography
                      component={'span'}
                      sx={{ color: '#3A4DE9', fontSize: '14px', cursor: 'pointer' }}
                      onClick={(e): void => handleOrderClick(e, order.id)}
                    >
                      {order.readable_id}{' '}
                      {tripOrders &&
                      tripOrders?.length > 0 &&
                      tripOrders?.findIndex((o) => o.id === order.id) !== tripOrders?.length - 1
                        ? ', '
                        : ''}
                    </Typography>
                  </TwLoadTooltipInfo>
                </Box>
              </>
            ))}
          </Box>
        </>
      )}
    </Box>
  );
};

export default TripOverview;
